import React from 'react';
import {
  Users,
  UserPlus,
  Gift,
  Star,
  CreditCard,
  Calendar,
  Link2,
  TrendingUp,
  Percent,
  Coins,
} from 'lucide-react';
import {
  SectionCard,
  InfoRow,
  MetricCard,
  TabLoading,
  TabError,
  TabEmpty,
  UserMiniCard,
  StatusBadge,
  getGenericStatusVariant,
} from './shared';
import { useUserReferrals } from '../../hooks/useUserTabs';
import { cn, formatDateTime, formatRelativeTime, formatCents, formatNumber } from '../../utils/helpers';

interface ReferralMade {
  id: number;
  referee: { id: number; name: string; email: string };
  referralCode: string;
  status: string;
  qualifiedAt: string | null;
  creditsIssuedAt: string | null;
  createdAt: string;
}

interface ReferralReceived {
  id: number;
  referrer: { id: number; name: string; email: string };
  referralCode: string;
  status: string;
  createdAt: string;
}

interface ReferralCreditItem {
  id: number;
  amountCents: number;
  remainingCents: number;
  status: string;
  sourceType: string;
  expiresAt: string | null;
  createdAt: string;
}

interface InfluencerProfile {
  id: number;
  status: string;
  commissionRate: number;
  totalEarnings: number;
  createdAt: string;
}

interface ReferralsData {
  referralsMade: ReferralMade[];
  referralReceived: ReferralReceived | null;
  referralCredits: ReferralCreditItem[];
  influencerProfile: InfluencerProfile | null;
}

interface ReferralsTabProps {
  userId: number;
}

const ReferralsTab: React.FC<ReferralsTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserReferrals(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No referral data available" icon={Users} />;

  const typedData = data as ReferralsData;
  const { referralsMade, referralReceived, referralCredits, influencerProfile } = typedData;

  const totalCreditValue = referralCredits.reduce((sum, c) => sum + c.amountCents, 0);

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          label="Referrals Made"
          value={formatNumber(referralsMade.length)}
          icon={UserPlus}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Credits Earned"
          value={formatNumber(referralCredits.length)}
          icon={Gift}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
        <MetricCard
          label="Total Credit Value"
          value={formatCents(totalCreditValue)}
          icon={CreditCard}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
      </div>

      {/* Referred By */}
      <SectionCard title="Referred By" icon={Link2} subtitle="Who referred this user">
        {referralReceived ? (
          <div className="space-y-3">
            <UserMiniCard
              user={{
                id: referralReceived.referrer.id,
                name: referralReceived.referrer.name,
                email: referralReceived.referrer.email,
              }}
              subtitle={`Code: ${referralReceived.referralCode}`}
              badge={
                <StatusBadge
                  status={referralReceived.status}
                  variant={getGenericStatusVariant(referralReceived.status)}
                />
              }
            />
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground px-1">
              <Calendar className="h-3 w-3" />
              <span>Referred on {formatDateTime(referralReceived.createdAt)}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-6">
            <p className="text-sm text-muted-foreground">Organic signup - no referrer</p>
          </div>
        )}
      </SectionCard>

      {/* Referrals Made */}
      <SectionCard
        title="Referrals Made"
        icon={UserPlus}
        subtitle={`${referralsMade.length} referral${referralsMade.length !== 1 ? 's' : ''}`}
        collapsible
        defaultOpen={referralsMade.length > 0}
      >
        {referralsMade.length > 0 ? (
          <div className="space-y-2.5">
            {referralsMade.map((referral) => (
              <div
                key={referral.id}
                className="rounded-lg border border-border/50 bg-muted/5 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <UserMiniCard
                      user={{
                        id: referral.referee.id,
                        name: referral.referee.name,
                        email: referral.referee.email,
                      }}
                      badge={
                        <StatusBadge
                          status={referral.status}
                          variant={getGenericStatusVariant(referral.status)}
                        />
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-3 flex-wrap px-1">
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Link2 className="h-3 w-3" />
                    <span className="font-mono">{referral.referralCode}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formatRelativeTime(referral.createdAt)}</span>
                  </div>
                  {referral.qualifiedAt && (
                    <div className="flex items-center gap-1 text-[11px] text-emerald-400/70">
                      <Star className="h-3 w-3" />
                      <span>Qualified {formatRelativeTime(referral.qualifiedAt)}</span>
                    </div>
                  )}
                  {referral.creditsIssuedAt && (
                    <div className="flex items-center gap-1 text-[11px] text-violet-400/70">
                      <Gift className="h-3 w-3" />
                      <span>Credits issued {formatRelativeTime(referral.creditsIssuedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-6">
            <p className="text-sm text-muted-foreground">No referrals made yet</p>
          </div>
        )}
      </SectionCard>

      {/* Referral Credits */}
      <SectionCard
        title="Referral Credits"
        icon={Coins}
        subtitle={`${referralCredits.length} credit${referralCredits.length !== 1 ? 's' : ''}`}
        collapsible
        defaultOpen={referralCredits.length > 0}
      >
        {referralCredits.length > 0 ? (
          <div className="space-y-2.5">
            {referralCredits.map((credit) => (
              <div
                key={credit.id}
                className="rounded-lg border border-border/50 bg-muted/5 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        {formatCents(credit.amountCents)}
                      </p>
                      <StatusBadge
                        status={credit.status}
                        variant={getGenericStatusVariant(credit.status)}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Source: {credit.sourceType.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono">#{credit.id}</span>
                </div>

                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  <div className="text-[11px] text-muted-foreground">
                    <span className="text-muted-foreground/60">Remaining: </span>
                    <span className={cn(
                      'font-medium',
                      credit.remainingCents > 0 ? 'text-emerald-400' : 'text-muted-foreground'
                    )}>
                      {formatCents(credit.remainingCents)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formatRelativeTime(credit.createdAt)}</span>
                  </div>
                  {credit.expiresAt && (
                    <div className="flex items-center gap-1 text-[11px] text-amber-400/70">
                      <Calendar className="h-3 w-3" />
                      <span>Expires {formatDateTime(credit.expiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-6">
            <p className="text-sm text-muted-foreground">No referral credits earned</p>
          </div>
        )}
      </SectionCard>

      {/* Influencer Profile */}
      {influencerProfile && (
        <SectionCard
          title="Influencer Profile"
          icon={TrendingUp}
          subtitle="Influencer program details"
        >
          <div className="space-y-1">
            <InfoRow
              label="Status"
              icon={Star}
              value={
                <StatusBadge
                  status={influencerProfile.status}
                  variant={getGenericStatusVariant(influencerProfile.status)}
                />
              }
            />
            <InfoRow
              label="Commission Rate"
              icon={Percent}
              value={`${(influencerProfile.commissionRate * 100).toFixed(1)}%`}
            />
            <InfoRow
              label="Total Earnings"
              icon={CreditCard}
              value={formatCents(influencerProfile.totalEarnings)}
            />
            <InfoRow
              label="Member Since"
              icon={Calendar}
              value={formatDateTime(influencerProfile.createdAt)}
            />
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default ReferralsTab;
